// Ultimate Card Game (UCG) - Neustart "Marine Null" balance constants
//
// Single source of truth for every tunable number in the stripped-down
// ruleset (js/data/cards.js's disabled cards, the removed mineral/CO-power
// economy, spell-free draw-on-demand). Nothing below should be duplicated
// as a literal anywhere else - see NEUSTART_PLAN.html for the reasoning
// behind each value.

// Flat draw rates are now the standard game mode.
// Optional URL switch to use legacy tiered rarity-weighted odds instead:
// ?rarityDraw=1, ?rarityOdds=1, ?weightedDraw=1, or ?rarity=1 (or ?flatDraw=0 / ?flatOdds=0)
const isBrowser = typeof window !== 'undefined' && typeof window.location !== 'undefined';
const isLegacyRarityRequested = isBrowser && (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has('rarityDraw') ||
      params.has('rarityOdds') ||
      params.has('weightedDraw') ||
      params.has('rarity') ||
      params.get('flatDraw') === '0' ||
      params.get('flatOdds') === '0';
  } catch {
    return false;
  }
})();

export const IS_FLAT_DRAW_ENABLED = !isLegacyRarityRequested;

export const BALANCE = {
  // Economy - plasma is the only resource left.
  startPlasma: 20,
  plasmaRate: 2.2,     // passive income per second
  maxPlasma: 150,      // hard cap - the actual brake on burst plays now
  drawCost: 2,           // plasma per manual draw
  maxHandSize: 5,
  startHandSize: 5,      // hand starts full so the very first decision is which order to play/draw in

  // Territorial control bonus (js/engine/battlefield.js).
  territoryMaxBonus: 0.2,  // quadratic curve, see NEUSTART_PLAN.html Phase 5
  territoryLerpSpeed: 6,     // visual smoothing only, not the hold logic itself
  territoryStillstandDelay: 3, // seconds of standstill before frontline slowly returns to center
  territoryDecaySpeed: 30,   // px/sec return speed toward center after standstill delay
  territoryDeadZone: 15,     // px neutral band around midline (800) to prevent flip-flop jitter

  // Superweapon strike - race-neutral, 1x per match unconditional.
  superweaponChargesPerMatch: 1,

  // Base auto-cannon damage per shot (js/engine/battlefield.js's
  // initBases()). Originally 45 - reduced 60% per user correction so a
  // single base volley no longer trades so heavily against a lone Marine.
  baseDamage: 18, // = 18 (was 45 * 0.4)

// Card draw probability weights by rarity (Two-Stage Tiered Draw).
  // Normalized dynamically over active/available card pools.
  rarityWeights: {
    Common: 54,
    Uncommon: 30,
    Rare: 13,
    Epic: 3
  },

  // Flat draw rates (experimental): when true, draws cards uniformly at random from available pool.
  flatDrawRates: IS_FLAT_DRAW_ENABLED,

  // Heavy-Card Anti-Lockout (docs/INVARIANTS.md §19):
  // Excludes cards costing >= 40 plasma from the draw pool if the player already holds >= 3 in hand.
  heavyCardLockCostThreshold: 40,
  heavyCardLockMaxCopies: 3
};

// Seconds from superweapon trigger to the board wipe, per base race: the
// Human warhead's flight time and the Sul Bio-Plasma's guaranteed final wipe
// (js/engine/particles.js). Everything played inside this window dies too,
// so the bot AI and the headless sims (js/admin/headlessSim.js) read the
// same numbers the particle engine uses.
// Deliberately NOT part of BALANCE: BALANCE feeds BALANCE_HASH, the online
// client/server version check, and this delay only exists offline - the
// authoritative server (server/gameSimulation.js) wipes the board in the
// same tick the superweapon is fired.
export const SUPERWEAPON_IMPACT_DELAY = { Human: 1.2, Sul: 1.95 };

// Post-match "Gamerank" skill grade (D..S+) - see docs/GAMERANK_PLAN.md for
// the full design rationale. Reads only from MatchStats.getSummary(), so it
// stays deterministic/lockstep-safe (docs/GAMERANK_PLAN.md section 9).
export const GAMERANK = {
  // Balanced 5-pillar skill rating: territory (15%), damage (25%), resource efficiency (25%),
  // base defense / HP preservation (20%), kill-to-death ratio (15%). Sums to 1.00 (100%).
  weights: { territory: 0.15, damage: 0.25, efficiency: 0.25, baseHp: 0.20, kdRatio: 0.15 },
  winBonus: 3,
  noSuperweaponBonus: 3, // +3 tactical discipline for winning without superweapon deployment
  // Speedrun tempo bonus for decisive fast victories
  tempoBonus: { fastThreshold: 180, mediumThreshold: 270, fastPoints: 2, mediumPoints: 1 },
  // Economic discipline bonus: +1 point if plasma never hit the 150 hard-cap (no wasted income)
  plasmaDisciplineBonus: 1,
  // Territory control % calibration (45% floor to 65% ceiling = 0..100 index).
  // Accounts for natural 50% start line and map geometry.
  territoryCalibration: { low: 45, high: 65 },
  // Damage/minute -> 0-100 index anchors.
  damageCalibration: { low: 480, high: 980 },
  // Resource efficiency ratio (materialValueDestroyed / plasmaSpent) -> 0-100 index anchors.
  efficiencyCalibration: { low: 0.45, high: 0.85 },
  // Base HP preservation -> 0..100 index (ratio of minOwnBaseHp to maxHp)
  baseHpCalibration: { maxHp: 1500 },
  // Kill-to-death ratio -> 0..100 index (1.0 = parity = 0 index, 3.0+ = dominant 3:1 = 100 index)
  kdCalibration: { low: 1.0, high: 3.0 },
  // Grade thresholds (D is assigned below c or via antiAbuse triggers)
  // Granular 9-step ladder: D, C, B, B+, A, A+, S-, S, S+
  thresholds: { sPlus: 97, s: 93, sMinus: 87, aPlus: 79, a: 68, bPlus: 55, b: 40, c: 20 },
  // Warmup duration (seconds) before live HUD projection begins (prevents instant early spikes)
  warmupSeconds: 20,
  // Anti-abuse safety net: matches surrendered/ended under minDurationSeconds
  // or with zero combat actions yield Rank D (0 Fugazi, 0 XP) to prevent reset farming.
  antiAbuse: { minDurationSeconds: 45, minActionsOrDamage: 1 }
};

// Unit MVP Spotlight: cost-normalized "best unit of the match" highlight -
// see docs/UNIT_MVP_PLAN.md for the full design rationale (formula section 3,
// weight placeholders section 8). Reads only from MatchStats.unitRecords.
export const UNIT_MVP = {
  weights: { damage: 1.0, killCredit: 2.0, healing: 1.0, damageTaken: 0.5 },
  costFloor: 5,
  minLifetimeSeconds: 2,
  minRawImpact: 10
};
